<template>
  <!-- El segundo tramo del flujo que empieza en "recuperar contraseña": acá se
       llega desde el enlace del correo. Mismos estados y mismo lenguaje que esa
       pantalla; lo que cambia es que antes de mostrar nada hay que preguntarle
       a la API si el enlace todavía sirve. -->
  <div class="af-wrap">
    <transition name="af-swap" mode="out-in">

      <!-- ══ 1. VERIFICANDO EL ENLACE ══
           Antes se mostraba el formulario mientras tanto y, si el enlace no
           servía, se lo reemplazaba por el error: alcanzaba a verse un formulario
           que nunca se iba a poder usar. -->
      <div v-if="state === 'checking'" key="checking" class="af-stage af-stage--center">
        <div class="af-mark af-mark--wait" role="status" :aria-label="$t('restore.checking')">
          <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle class="af-mark__ring" cx="32" cy="32" r="29.5" />
            <circle class="af-mark__arc" cx="32" cy="32" r="29.5" />
          </svg>
        </div>
        <span class="af-eyebrow">{{ $t('restore.checking') }}</span>
      </div>

      <!-- ══ 2. LA CONTRASEÑA NUEVA ══ -->
      <div v-else-if="state === 'form'" key="form" class="af-stage">
        <p class="af-intro">{{ $t('restore.intro') }}</p>

        <!-- Sin <form>, por el mismo motivo que en Recuperar.vue: un envío
             nativo recarga la página (y deja el `?` en la URL) justo cuando el
             POST está viajando, y un form.submit() hecho por código ajeno no
             emite evento, así que no hay forma de prevenirlo. El Enter lo
             maneja este contenedor. -->
        <div @keydown.enter.prevent="onSubmit">
          <!-- Campo 1 -->
          <label class="cc-label" for="cc-new">{{ $t('restore.new-password') }}</label>
          <div class="cc-field" :class="{ 'is-on': focus === 'new' }">
            <input
              id="cc-new"
              ref="first"
              :type="reveal ? 'text' : 'password'"
              class="cc-input"
              v-model="pass"
              :placeholder="$t('restore.enter-new-password')"
              autocomplete="new-password"
              @focus="focus = 'new'"
              @blur="focus = ''"
            />
            <!-- Un solo ojo para los dos campos: son la misma contraseña, y
                 esconder una mientras la otra se ve no protege nada. -->
            <button
              type="button"
              class="cc-eye"
              :class="{ 'is-on': reveal }"
              :title="reveal ? $t('restore.hide') : $t('restore.show')"
              :aria-label="reveal ? $t('restore.hide') : $t('restore.show')"
              :aria-pressed="String(reveal)"
              @click="reveal = !reveal"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12z" />
                <circle cx="12" cy="12" r="3.1" />
                <!-- La barra se dibuja sola al tapar: el ojo no cambia de forma,
                     se tacha, que es lo que dice "ahora está oculto". -->
                <path v-if="reveal" class="cc-eye__slash" d="M4 20 L20 4" />
              </svg>
            </button>
          </div>

          <!-- Requisitos, en vivo. Es lo que antes aparecía como un párrafo rojo
               recién después de equivocarse. -->
          <ul class="cc-rules">
            <li v-for="r in rules" :key="r.key" :class="{ 'is-ok': r.ok }">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3.5 8.5 L6.5 11.5 L12.5 4.5" />
              </svg>
              {{ $t('restore.' + r.key) }}
            </li>
          </ul>

          <!-- Campo 2 -->
          <label class="cc-label cc-label--2" for="cc-repeat">{{ $t('restore.repeat-new-password') }}</label>
          <div
            class="cc-field"
            :class="{ 'is-on': focus === 'repeat', 'is-bad': repeatTouched && !sameOk, 'is-good': sameOk }"
          >
            <input
              id="cc-repeat"
              :type="reveal ? 'text' : 'password'"
              class="cc-input"
              v-model="repeat"
              :placeholder="$t('restore.repeat-new-password')"
              autocomplete="new-password"
              @focus="focus = 'repeat'"
              @blur="focus = ''; repeatTouched = true"
            />
            <!-- El visto aparece cuando las dos coinciden: cierra el campo sin
                 tener que leer nada. -->
            <span v-if="sameOk" class="cc-ok" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none">
                <path d="M3.5 8.5 L6.5 11.5 L12.5 4.5" />
              </svg>
            </span>
          </div>
          <p v-if="repeatTouched && !sameOk" class="cc-bad">{{ $t('restore.passwords-same') }}</p>

          <!-- Lo que falló del lado del servidor va en su propio bloque, y no
               como una línea más debajo de los campos: no es un error de lo que
               se escribió, y mezclarlo ahí hacía pensar que la contraseña
               estaba mal. -->
          <div v-if="apiError" class="cc-alert" role="alert">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="10" cy="10" r="8.4" />
              <path d="M10 6 L10 10.6" />
              <path d="M10 13.6 L10 13.9" />
            </svg>
            <span>{{ apiError }}</span>
          </div>

          <button type="button" class="btn-auth-gradient af-send" :disabled="!canSubmit" @click="onSubmit">
            {{ $t('restore.save') }}
          </button>
        </div>
      </div>

      <!-- ══ 3. EL ENLACE NO SIRVE ══
           Cada motivo dice algo distinto y termina en la acción que
           corresponde, que casi siempre es pedir un enlace nuevo. -->
      <div v-else key="dead" class="af-stage af-stage--center">
        <div class="af-mark af-mark--dead" aria-hidden="true">
          <svg viewBox="0 0 64 64" fill="none">
            <circle class="af-mark__ring" cx="32" cy="32" r="29.5" />
            <path class="af-mark__bar" d="M22 42 L42 22" />
          </svg>
        </div>

        <h2 class="af-title">{{ $t(deadCode === 'NETWORK' ? 'restore.dead-network-title' : 'restore.dead-title') }}</h2>
        <p class="af-lead">{{ $t(deadMessageKey) }}</p>

        <button v-if="deadCode === 'NETWORK'" type="button" class="btn-auth-gradient af-send" @click="verify">
          {{ $t('restore.retry') }}
        </button>
        <router-link v-else :to="{ name: 'recuperar' }" class="btn-auth-gradient af-send af-send--link">
          {{ $t('restore.ask-new-link') }}
        </router-link>

        <router-link :to="{ name: 'login' }" class="af-quiet">{{ $t('recover.back-to-login') }}</router-link>
      </div>

    </transition>

    <!-- ══ POP-UP DE GUARDADO ══
         Tapa la pantalla a propósito: mientras se escribe la contraseña nueva
         no hay nada más que hacer en esta página, y cuando termina tampoco
         —de acá se sale al login. Misma pieza que el modal de bienvenida del
         registro, para que el sitio se comporte igual en los dos cierres.

         El velo es position:fixed y se mide contra la ventana. Para eso ningún
         ancestro puede tener transform/filter activo: la animación de entrada
         del layout lo tenía (ver el comentario del `forwards` en Layout.vue) y
         el velo quedaba recortado al ancho del formulario. -->
    <transition name="cc-pop">
      <div v-if="modal" class="cc-overlay" role="dialog" aria-modal="true" aria-live="polite">
        <div class="cc-card">

          <template v-if="modal === 'saving'">
            <div class="af-mark af-mark--wait cc-card__mark">
              <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <circle class="af-mark__ring" cx="32" cy="32" r="29.5" />
                <circle class="af-mark__arc" cx="32" cy="32" r="29.5" />
              </svg>
            </div>
            <h2 class="cc-card__title">{{ $t('restore.saving') }}</h2>
          </template>

          <template v-else>
            <div class="af-mark af-mark--done cc-card__mark">
              <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <circle class="af-mark__ring" cx="32" cy="32" r="29.5" />
                <path class="af-mark__check" d="M19 33.5 L28.5 43 L46 22" />
              </svg>
            </div>
            <h2 class="cc-card__title">{{ $t('restore.done-title') }}</h2>
            <p class="cc-card__text">{{ $t('restore.redirecting') }}</p>
            <div class="cc-dots" aria-hidden="true"><span></span><span></span><span></span></div>
            <!-- Los tres segundos son para leer, no para esperar: el que ya
                 leyó se va cuando quiere. -->
            <button type="button" class="cc-card__now" @click="toLogin">{{ $t('restore.go-now') }}</button>
          </template>

        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios'

/* Lo que dura como mínimo el pop-up de "guardando". La API suele contestar en
   menos, y sin el piso el cartel aparece y desaparece en un parpadeo. */
const MIN_WAIT_MS = 900

/* Cuánto se queda el "contraseña actualizada" antes de ir al login. */
const REDIRECT_MS = 3000

/* Las reglas de la contraseña. Son las que ya pedía esta pantalla; lo que
   cambia es que ahora se ven mientras se escribe en vez de aparecer en rojo
   después de mandar. Ojo: la API NO las valida —solo exige que los dos campos
   coincidan—, así que esto es lo único que las sostiene. */
const RULES = [
  { key: 'req-length', test: v => v.length >= 6 && v.length <= 10 },
  { key: 'req-upper',  test: v => /[A-Z]/.test(v) },
  { key: 'req-lower',  test: v => /[a-z]/.test(v) },
  { key: 'req-number', test: v => /[0-9]/.test(v) }
]

export default {
  name: 'CambiarClave',

  data () {
    return {
      // checking | form | dead
      state: 'checking',
      // null | saving | done  → el pop-up
      modal: null,
      // Por qué no sirve el enlace: lo que contesta la API en `code`, o NETWORK
      // si directamente no hubo respuesta.
      deadCode: '',
      pass: '',
      repeat: '',
      repeatTouched: false,
      reveal: false,
      focus: '',
      apiError: '',
      _redirect: null
    }
  },

  computed: {
    token () { return this.$route.params.token },
    rules () {
      return RULES.map(r => ({ key: r.key, ok: r.test(this.pass) }))
    },
    passOk () {
      return this.rules.every(r => r.ok)
    },
    sameOk () {
      return this.repeat.length > 0 && this.pass === this.repeat
    },
    canSubmit () {
      return this.passOk && this.sameOk
    },
    deadMessageKey () {
      const map = {
        TOKEN_USED: 'restore.dead-used',
        TOKEN_EXPIRED: 'restore.dead-expired',
        TOKEN_NOT_FOUND: 'restore.dead-notfound',
        NETWORK: 'restore.dead-network'
      }
      return map[this.deadCode] || 'restore.dead-gone'
    }
  },

  created () {
    window.scrollTo(0, 0)
    this.verify()
  },

  /* El timer sobrevive al componente si el usuario se va antes de los tres
     segundos, y entonces lo manda al login desde donde sea que esté. */
  beforeDestroy () {
    if (this._redirect) clearTimeout(this._redirect)
    document.body.style.overflow = ''
  },

  methods: {
    /**
     * ¿El enlace todavía sirve? La API distingue entre usado, vencido y
     * inexistente, así que el motivo se muestra tal cual: "pedí otro" no
     * significa lo mismo si ya lo usaste que si venció.
     */
    async verify () {
      this.state = 'checking'
      const empezo = Date.now()
      try {
        const { data } = await axios.get(process.env.VUE_APP_API_URL + 'check/token/' + this.token)
        await this.hold(empezo)
        if (data && data.disponible) {
          this.state = 'form'
          this.$nextTick(() => { if (this.$refs.first) this.$refs.first.focus() })
        } else {
          this.deadCode = (data && data.code) || ''
          this.state = 'dead'
        }
      } catch (e) {
        await this.hold(empezo)
        this.deadCode = 'NETWORK'
        this.state = 'dead'
      }
    },
    /**
     * Guarda la contraseña nueva. Entre el check y este POST puede haber pasado
     * de todo —el enlace pudo usarse en otra pestaña, o vencer—, y en ese caso
     * la API contesta 404: eso no es un error del formulario, así que se vuelve
     * a la pantalla del enlace caído en vez de dejar un mensaje rojo en un
     * formulario que ya no sirve.
     */
    async onSubmit (e) {
      // Cinturón y tiradores: el modificador .prevent del form ya lo hace, pero
      // si esta navegación se escapa la página se recarga y el POST se corta a
      // la mitad. Es exactamente lo que estaba pasando.
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      if (!this.canSubmit || this.modal) return

      this.apiError = ''
      this.openModal('saving')
      const empezo = Date.now()

      try {
        await axios.post(process.env.VUE_APP_API_URL + 'change/password', {
          new_password: this.pass,
          check_password: this.repeat,
          token: this.token
        })
        await this.hold(empezo)
        this.pass = ''
        this.repeat = ''
        this.modal = 'done'
        this._redirect = setTimeout(this.toLogin, REDIRECT_MS)
      } catch (e) {
        await this.hold(empezo)
        this.closeModal()
        const res = e && e.response
        const status = res ? res.status : 0

        // El token murió entre el check y el guardado: no es un problema del
        // formulario, así que se muestra la pantalla del enlace caído.
        if (status === 404) {
          this.deadCode = 'TOKEN_EXPIRED'
          this.state = 'dead'
          return
        }

        this.apiError = this.apiMessage(status, res && res.data)
        // Queda en la consola lo que contestó el servidor: el texto de la
        // pantalla está escrito para la persona, no para depurar.
        console.error('[CambiarClave] change/password falló', status, res && res.data)
      }
    },
    /**
     * Qué mostrar cuando el guardado no salió. Cada caso dice algo distinto
     * porque llevan a acciones distintas: reintentar, revisar lo escrito, o
     * probar directamente con la contraseña nueva.
     */
    apiMessage (status, data) {
      // Sin respuesta: la API no está levantada, se cayó la red, o lo frenó
      // CORS. Decir "no pudimos guardar" a secas manda a buscar el problema en
      // el formulario, que es donde no está.
      if (!status) return this.$t('restore.error-network')

      // La API contesta `error` (string) o `errors` (campo → mensajes). Si
      // mandó un motivo, ese motivo es mejor que cualquier texto nuestro.
      if (data && typeof data.error === 'string' && data.error) return data.error
      if (data && data.errors) {
        const first = Object.keys(data.errors)[0]
        const msg = first && data.errors[first]
        if (Array.isArray(msg) && msg.length) return msg[0]
        if (typeof msg === 'string') return msg
      }

      // 500: el cambio pudo haberse aplicado igual —la API guarda la contraseña
      // y recién después manda el correo de confirmación, así que un fallo de
      // correo revienta la respuesta con el cambio ya hecho.
      if (status >= 500) return this.$t('restore.error-server')

      return this.$t('restore.error-generic')
    },
    toLogin () {
      if (this._redirect) { clearTimeout(this._redirect); this._redirect = null }
      this.closeModal()
      this.$router.push({ name: 'login' })
    },
    /* Con el pop-up abierto el fondo no se scrollea: es una pantalla completa,
       no una capa más sobre el formulario. */
    openModal (kind) {
      this.modal = kind
      document.body.style.overflow = 'hidden'
    },
    closeModal () {
      this.modal = null
      document.body.style.overflow = ''
    },
    hold (empezo) {
      const resto = MIN_WAIT_MS - (Date.now() - empezo)
      if (resto <= 0) return Promise.resolve()
      return new Promise(resolve => setTimeout(resolve, resto))
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./auth-flow";

/* ── El formulario ───────────────────────────────────────────
   Campos propios en vez del .form-control de Bootstrap: el resto de la
   pantalla ya no es Bootstrap, y el input gris de 38px desentonaba con la
   pastilla del botón y con el tipo de Geist. */
.cc-label {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: $af-ink;
}
.cc-label--2 {
  margin-top: 20px;
}

.cc-field {
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 6px 0 14px;
  border: 1px solid $af-border;
  border-radius: 12px;
  background: #FAFAF9;
  transition: border-color 0.16s, background 0.16s, box-shadow 0.16s;

  /* El foco se marca con el naranja de la marca y un halo suave: es el único
     color que aparece mientras se escribe. */
  &.is-on {
    background: #fff;
    border-color: rgba(252, 112, 16, 0.55);
    box-shadow: 0 0 0 3px rgba(252, 112, 16, 0.12);
  }
  &.is-good { border-color: rgba(252, 112, 16, 0.45); }
  &.is-bad {
    border-color: rgba(185, 30, 124, 0.55);
    &.is-on { box-shadow: 0 0 0 3px rgba(185, 30, 124, 0.12); }
  }
}

.cc-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  background: none;
  outline: none;
  font-family: inherit;
  font-size: 14.5px;
  letter-spacing: -0.01em;
  color: $af-ink;

  &::placeholder { color: $af-faint; letter-spacing: 0; }
  /* La contraseña oculta se ve mejor con los puntos un poco separados. */
  &[type="password"] { letter-spacing: 0.14em; font-size: 15px; }
}

/* ── El ojo ─────────────────────────────────────────────────── */
.cc-eye {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: none;
  color: $af-faint;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  svg {
    width: 19px;
    height: 19px;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }

  &:hover { color: $af-ink; background: rgba(10, 10, 11, 0.05); }
  &.is-on { color: $af-orange; }
}

/* La tachadura se dibuja en vez de aparecer de golpe: el ojo es el mismo, lo
   que cambia es el estado. */
.cc-eye__slash {
  stroke-dasharray: 23;
  animation: cc-slash 0.2s ease-out both;
}
@keyframes cc-slash {
  from { stroke-dashoffset: 23; }
  to   { stroke-dashoffset: 0; }
}

/* ── El visto del "repetir" ─────────────────────────────────── */
.cc-ok {
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
    stroke: $af-orange;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    animation: cc-pop-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
}
@keyframes cc-pop-in {
  from { opacity: 0; transform: scale(0.6); }
}

/* ── Requisitos ──────────────────────────────────────────────
   Cuatro líneas que se van marcando solas. Apagados son grises; al cumplirse
   se pintan del naranja de la marca. */
.cc-rules {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 6px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.4;
    color: $af-faint;
    transition: color 0.18s;
  }

  svg {
    flex: 0 0 15px;
    width: 15px;
    height: 15px;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0.32;
    transition: opacity 0.18s, transform 0.18s;
  }

  .is-ok {
    color: $af-orange;
    svg { opacity: 1; transform: scale(1.06); }
  }
}

.cc-bad {
  margin: 7px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #B91E7C;
}

/* Falla del servidor: bloque propio, con su marca, separado de los campos. */
.cc-alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 18px;
  padding: 11px 13px;
  border: 1px solid rgba(185, 30, 124, 0.28);
  border-radius: 11px;
  background: rgba(185, 30, 124, 0.06);
  font-size: 12.8px;
  line-height: 1.45;
  color: #8E1760;

  svg {
    flex: 0 0 17px;
    width: 17px;
    height: 17px;
    margin-top: 1px;
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linecap: round;
    fill: none;
  }
}

/* El botón apagado tiene que verse apagado: el gradiente con sombra de color
   invita a apretarlo aunque no haga nada. */
.btn-auth-gradient:disabled {
  background: #E6E4DF;
  color: #A0A0A8;
  box-shadow: none;
  cursor: not-allowed;

  &:hover { transform: none; box-shadow: none; }
}

/* ── El pop-up ──────────────────────────────────────────────── */
.cc-overlay {
  position: fixed;
  /* Las cuatro por separado además de `inset`: Safari recién entiende `inset`
     desde la 14.1, y sin esto el velo se encoge al tamaño de su contenido. */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(10, 10, 11, 0.82);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.cc-card {
  width: 100%;
  max-width: 400px;
  padding: 44px 40px 36px;
  border-radius: 24px;
  background: #fff;
  text-align: center;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.28);
}

.cc-card__mark {
  margin: 0 auto 22px;
}

.cc-card__title {
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: $af-ink;
}

.cc-card__text {
  margin: 10px 0 0;
  font-size: 14px;
  color: $af-dim;
}

.cc-card__now {
  margin-top: 18px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: $af-dim;
  cursor: pointer;
  transition: color 0.15s;

  &:hover { color: $af-ink; }
}

/* Los tres puntos son los mismos del modal de bienvenida del registro: la
   espera se lee igual en los dos lados del sitio. */
.cc-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 16px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $af-orange;
    animation: cc-dot 1.2s ease-in-out infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}
@keyframes cc-dot {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1.1); }
}

.cc-pop-enter-active { transition: opacity 0.2s ease; }
.cc-pop-leave-active { transition: opacity 0.16s ease; }
.cc-pop-enter, .cc-pop-leave-to { opacity: 0; }
.cc-pop-enter .cc-card { transform: scale(0.94); }
.cc-card { transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1); }

@media (prefers-reduced-motion: reduce) {
  .cc-eye__slash,
  .cc-ok svg,
  .cc-dots span { animation: none; }
  .cc-card { transition: none; }
}

@media (max-width: 480px) {
  .cc-card { padding: 36px 26px 30px; }
  /* 16px es el piso: por debajo de eso Safari en iPhone hace zoom solo al
     enfocar el campo y deja la pantalla corrida, con el botón fuera de la
     vista. El tamaño de lectura no cambia en escritorio. */
  .cc-input { font-size: 16px; }
  .cc-input[type="password"] { font-size: 16px; }
}
</style>
